import { Injectable } from '@angular/core';
import { ContextDossier } from '@kv-common-components/kv-common-components';
import { RouteEnum } from '@shared/services/route-enum';
import { RouterHelperService } from '@shared/services/router-helper.service';
import { AssuranceHelperService } from '@shared/services/assurance-helper.service';
import {
    AuthenticationService,
    QuestionnaireSanteService,
} from '@kv-common-components/kx-services';
import { take } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class RepriseHelperService {
    static readonly routesWithStepsAdhesionModal = [
        RouteEnum.consentement.toString(),
        RouteEnum.qss.toString(),
        RouteEnum.qs.toString(),
        RouteEnum.differedDecisionIntroduction.toString(),
        RouteEnum.documentWaiting.toString(),
        RouteEnum.signature.toString(),
    ];
    hideModalReprise = false;
    private stepsAdhesionModalDisplayed = false;

    constructor(
        private routerHelperService: RouterHelperService,
        private questionnaireSanteService: QuestionnaireSanteService,
        private authService: AuthenticationService
    ) {}

    /**
     * Permet de positionner l IHM EADE a la bonne etape (route) du parcours
     *
     * @param dossier
     */
    navigateToResumeStep(dossier: ContextDossier): void {
        const user = this.authService.currentUserValue;
        user.idDossier = dossier.dossier.idProjetCredit;
        user.idPropositionPrincipale =
            dossier.emprunteur.assurance.idPropositionPrincipale;
        this.authService.updateCurrentUser(user);
        this.resumeSteps(dossier).subscribe((route) => {
            this.routerHelperService.navigateByStepUrl(route);
            this.stepsAdhesionModalDisplayed =
                !RepriseHelperService.routesWithStepsAdhesionModal.includes(
                    route
                );
        });
    }

    /**
     * Retourne un observable contenant la bonne route associe au code AIA statut DA
     *
     * @param dossier
     */
    resumeSteps(dossier: ContextDossier): Observable<string> {
        return new Observable((observer) => {
            if (
                dossier.emprunteur.currentAssurance.statutDA ===
                AssuranceHelperService.qsEnAttenteDeSaisieInternet.codeAia
            ) {
                this.getResumeStepForStatutQsEnAttenteDeSaisieInternet(
                    dossier
                ).subscribe((route) => {
                    observer.next(route);
                    observer.complete();
                });
            } else if (
                AssuranceHelperService.isCodeAiaStatutDAForFinalisation(
                    dossier.emprunteur.currentAssurance.statutDA
                )
            ) {
                observer.next(RouteEnum.finalisation);
                observer.complete();
            } else if (
                AssuranceHelperService.isCodeAiaStatutDAForSignaturePropo(
                    dossier.emprunteur.currentAssurance.statutDA
                )
            ) {
                if (
                    dossier.emprunteur.currentAssurance.statutEcretement ===
                    AssuranceHelperService.statutEcretementEligible
                ) {
                    observer.next(RouteEnum.finalisation);
                    observer.complete();
                } else {
                    if (
                        dossier.emprunteur.currentAssurance.statutDA ===
                        AssuranceHelperService
                            .h2DossierAccepteAvecRestrictionDeGarantieAccordClientEnAttente
                            .codeAia
                    ) {
                        this.hideModalReprise = true;
                    }
                    observer.next(RouteEnum.signature);
                    observer.complete();
                }
            } else if (
                AssuranceHelperService.isCodeAiaStatutDAForUploadMedicalDocument(
                    dossier.emprunteur.currentAssurance.statutDA
                )
            ) {
                observer.next(RouteEnum.differedDecisionIntroduction);
                observer.complete();
            } else {
                observer.next(RouteEnum.dashboard);
                observer.complete();
            }
        });
    }

    /**
     * Retourne un observable contenant la bonne route associe au code AIA statut DA FC
     *
     * @param dossier
     */
    getResumeStepForStatutQsEnAttenteDeSaisieInternet(dossier: ContextDossier): Observable<string> {
        return new Observable((observer) => {
          const currentAssurance = dossier.emprunteur.currentAssurance;

          if (!currentAssurance.consentementInternet) {
            observer.next(RouteEnum.consentement);
            observer.complete();
            return;
          }

          if (currentAssurance.validationQuestionnaireSansDecision) {
            observer.next(RouteEnum.signature);
            observer.complete();
            return;
          }

          if (dossier.emprunteur.topSansSelectionMedicale) {
            this.routerHelperService
              .sendFakeQssLemoine(
                this.authService.currentUserValue.idPersonne.toString(),
                this.authService.currentUserValue.idDossier.toString()
              )
              .subscribe(() => {
                observer.next(RouteEnum.signature);
                observer.complete();
              });
            return;
          }

          const hasSanteQuestions = currentAssurance.listeReponsesQuestionsSante?.length > 0;
          const hasSanteSimplifieQuestions = currentAssurance.listeReponsesQuestionsSanteSimplifie?.length > 0;

          if (hasSanteQuestions) {
            observer.next(RouteEnum.qs);
            observer.complete();
            return;
          }

          if (hasSanteSimplifieQuestions) {
            observer.next(RouteEnum.qss);
            observer.complete();
            return;
          }

          this.questionnaireSanteService.initializeData(
            dossier.emprunteur.idPersonne,
            dossier.dossier.idProjetCredit
          );

          this.questionnaireSanteService.currentQuestionnaireSante
            .pipe(take(1))
            .subscribe((data) => {
              const route = (data.listeQuestionsQuestionnaireSanteSimplifie?.length > 0)
                ? RouteEnum.qss
                : RouteEnum.qs;


              observer.next(route);
              observer.complete();
            });
        });
      }

    isStepsAdhesionModalNeedToBeDisplay(): boolean {
        const needToBeDisplay = !this.stepsAdhesionModalDisplayed;
        this.stepsAdhesionModalDisplayed = true;
        return this.hideModalReprise ? false : needToBeDisplay;
    }
}
